import { type FC, Fragment, type MouseEvent, type ReactNode, type Ref } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';
import { Link } from 'dibs-elements/exports/Link.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Header } from 'dibs-elements/exports/Header.tsx';

import { viewMore } from '../../../shared/sharedMessages.jsx';
import { PageLayout } from 'dibs-elements/exports/PageLayout.tsx';

import { type PdpRespSimilarItemsContainer_item$key } from './__generated__/PdpRespSimilarItemsContainer_item.graphql.ts';

import styles from './PdpRespSimilarItemsContainer.scss';

const itemFragment = graphql`
    fragment PdpRespSimilarItemsContainer_item on Item {
        isSold
        seller {
            isAccessible
        }
    }
`;

type Props = {
    header?: ReactNode;
    subtitle?: ReactNode;
    subHeader?: ReactNode;
    children?: ReactNode;
    gridWrapperClass?: string;
    gridHeaderClass?: string;
    dataTn: string;
    item: PdpRespSimilarItemsContainer_item$key;
    scrollToRef?: Ref<HTMLDivElement>;
    isDark?: boolean;
    isHorizontal?: boolean;
    hideBar?: boolean;
    onClickViewMore?: (event?: MouseEvent | null) => void;
    isTopContainer?: boolean;
    isUnavailablePdp?: boolean;
    isSponsored?: boolean;
};

export const PdpRespSimilarItemsContainer: FC<Props> = ({
    header,
    subtitle,
    subHeader,
    children,
    dataTn,
    gridWrapperClass,
    gridHeaderClass,
    isUnavailablePdp,
    isHorizontal,
    item: itemRef,
    scrollToRef,
    isSponsored,
    isDark,
    hideBar,
    onClickViewMore,
    isTopContainer,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { seller, isSold } = item || {};
    const { isAccessible } = seller || {};
    const isNotListedOrSold = !isAccessible;

    const wrapperClass = classnames(styles.wrapper, {
        [styles.hasSubtitle]: !!subtitle,
        [styles.isTopContainer]: isTopContainer,
    });
    const contentClass = classnames(styles.content, gridWrapperClass, {
        [styles.isHorizontal]: isHorizontal,
    });
    const subtitleClass = classnames(isNotListedOrSold ? styles.subtitleSoldPdp : styles.subtitle, {
        [styles.hasHeader]: !!header,
    });
    const headerClass = classnames(styles.header, gridHeaderClass, {
        [styles.isUnavailablePdp]: isUnavailablePdp,
    });

    const pageLayoutProps = {
        setViewportBackground: isDark,
        setOffset: isDark,
    };

    return (
        <PageLayout {...pageLayoutProps} dataTn={dataTn}>
            {/* give wrapper tabIndex to allow focus on "view more" scroll to */}
            <div tabIndex={-1} ref={scrollToRef} className={wrapperClass}>
                {header && (
                    <HeadingLevel>
                        {Heading =>
                            isSold ? (
                                <Fragment>
                                    <div className={headerClass}>
                                        <Header
                                            htmlElementType={Heading}
                                            title={header}
                                            dataTn="dlos-header"
                                        />
                                    </div>
                                    {!hideBar && <div className={styles.soldHeader} />}
                                </Fragment>
                            ) : (
                                <Heading className={headerClass}>{header}</Heading>
                            )
                        }
                    </HeadingLevel>
                )}

                <div
                    className={contentClass}
                    data-tn={`pdp-similar${isSponsored ? '-sponsored' : ''}-items`}
                >
                    {!!subHeader && subHeader}
                    {!!subtitle && (
                        <div className={subtitleClass}>
                            {subtitle}
                            {onClickViewMore && (
                                <>
                                    {' '}
                                    <Link
                                        className={classnames(styles.viewMore, {
                                            [styles.viewMoreSoldPdp]: isNotListedOrSold,
                                        })}
                                        onClick={onClickViewMore}
                                        dataTn="view-more-similar-items"
                                    >
                                        {viewMore}
                                    </Link>
                                </>
                            )}
                        </div>
                    )}
                    {children}
                </div>
            </div>
        </PageLayout>
    );
};
